import { OktaAuth } from '@okta/okta-auth-js';
import { jwtDecode } from 'jwt-decode';
import {
  getAccessToken,
  getIdToken,
  isTokenExpired,
  setAccessToken,
  setLoggedUserDetails,
} from '../utils/authStorage';
import { TOKEN_NOT_ELIGIBLE_FOR_REFRESH } from '../utils/constants';
import type { AuthConfig } from '../types';

/**
 * Thin wrapper around @okta/okta-auth-js, ported behavior-for-behavior from the old
 * app's services/AuthService.js plus utils.js:validateAccessToken. Config now arrives
 * from /api/auth-config rather than a bundled property.json, so one build serves every
 * environment.
 *
 * tokenManager storage stays "localStorage" — a deliberate parity decision
 * (AuthService.js:15), not an oversight.
 */
export function createOktaAuth(config: AuthConfig): OktaAuth {
  return new OktaAuth({
    issuer: config.okta.issuer,
    clientId: config.okta.clientId,
    redirectUri: config.okta.redirectUri,
    scopes: config.okta.scopes,
    tokenManager: { storage: 'localStorage' },
  });
}

/**
 * AuthService.initLogin() (AuthService.js:19-43).
 *
 * Completes an in-flight Okta redirect if the URL carries auth params, otherwise reuses
 * a still-valid cached token, else starts the redirect login and returns false so the
 * caller stops rendering.
 *
 * One deliberate difference from the original: it wrote `localStorage.setItem(key,
 * accessToken)` with a possibly-undefined value, which stores the string "undefined" and
 * then fails to decode on the next read. Here an absent token simply is not written, so
 * the cached-token check below sees "no token" and re-runs the login — the outcome the
 * original stumbled into via a decode failure, reached deliberately instead.
 */
export async function initLogin(oktaAuth: OktaAuth, config: AuthConfig): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);

  if (params.get('code') || params.get('state') || params.get('id_token')) {
    const tokenResponse = await oktaAuth.token.parseFromUrl();
    const accessToken = tokenResponse.tokens.accessToken?.accessToken;
    const idToken = tokenResponse.tokens.idToken?.idToken;

    if (accessToken) setAccessToken(accessToken);
    // AuthService.js stored the raw id token itself; setLoggedUserDetails also decodes
    // it into `logged-user`, which App.js then re-read immediately afterwards.
    if (idToken) setLoggedUserDetails(idToken);

    window.history.replaceState({}, document.title, '/');
    return true;
  }

  const cached = getAccessToken();
  if (cached && !isTokenExpired(cached)) {
    return true;
  }

  login(oktaAuth, config);
  return false;
}

/** AuthService.getToken() (AuthService.js:45-60). */
export async function getToken(oktaAuth: OktaAuth, config: AuthConfig): Promise<string> {
  const cached = getAccessToken();
  if (cached && !isTokenExpired(cached)) {
    return cached;
  }

  const tokenResponse = await oktaAuth.token.getWithoutPrompt({ scopes: config.okta.scopes });
  const token = tokenResponse.tokens.accessToken?.accessToken;
  if (!token) {
    throw new Error('Okta did not return an access token');
  }
  setAccessToken(token);
  return token;
}

/**
 * utils.js:validateAccessToken (utils.js:32-48) — run before EVERY API call in the old
 * app, via generateHeaderDetails.
 *
 * A still-valid token is returned as-is. An expired one is refreshed, EXCEPT when the
 * session arrived from Empower (`?source=empower`): those sessions cannot be silently
 * renewed, so it throws TOKEN_NOT_ELIGIBLE_FOR_REFRESH, which reaches the user as
 * "Sorry your session expired. Please refresh the page to continue".
 *
 * Registered as the token resolver in AuthContext; see services/tokenProvider.ts.
 */
export async function validateAccessToken(
  oktaAuth: OktaAuth,
  config: AuthConfig,
): Promise<string | null> {
  const cached = getAccessToken();
  // The original called jwt(accessToken) unguarded, so a missing token threw a decode
  // error into the caller's catch and produced the generic message. Returning null sends
  // the request without an Authorization header instead, and the backend answers 400 —
  // a clearer path to the same "something is wrong, sign in again" outcome.
  if (!cached) return null;

  if (!isTokenExpired(cached)) return cached;

  const params = new URLSearchParams(window.location.search);
  if (params.get('source') === 'empower') {
    throw new Error(TOKEN_NOT_ELIGIBLE_FOR_REFRESH);
  }

  const refreshed = await getToken(oktaAuth, config);
  setAccessToken(refreshed);
  return refreshed;
}

export function login(oktaAuth: OktaAuth, config: AuthConfig): void {
  void oktaAuth.token.getWithRedirect({ scopes: config.okta.scopes });
}

/** AuthService.logout(): clears everything, then signs out of Okta. */
export function logout(oktaAuth: OktaAuth): void {
  localStorage.clear();
  void oktaAuth.signOut();
}

/**
 * The `?jwt=` deep link from Empower (App.js:45-62).
 *
 * Empower navigates here with the access token in the query string; the id token is
 * expected to already be in localStorage from a prior session on the same origin. The
 * original read `localStorage.getItem("idToken")` and passed it straight to jwt-decode,
 * which throws when it is absent — landing in App.js's catch and showing the error
 * modal. Reproduced: an absent id token still throws, because the alternative is a
 * signed-in-looking app with no user identity.
 */
export function acceptInjectedJwt(jwt: string): void {
  // Reject anything that is not a decodable JWT before it is stored or trusted.
  try {
    jwtDecode(jwt);
  } catch {
    throw new Error('Invalid injected session token');
  }
  setAccessToken(jwt);
  const idToken = getIdToken();
  if (!idToken) {
    throw new Error('No id token in storage for the injected session');
  }
  setLoggedUserDetails(idToken);
}
